/**
 * Home constants
 * Status options use DocumentStatusEnum directly from the API.
 * Type and Organization options are fetched dynamically from the API.
 */
import { DocumentStatusEnum } from "@/types/api/documents";

/**
 * Synthetic status filter id for retention-expired documents.
 * Not a DocumentStatusEnum value — matches `doc.isRetentionExpired` instead of `doc.apiStatus`.
 */
export const EXPIRED_STATUS_FILTER = "expired" as const;

/**
 * Document status filter options.
 * Uses DocumentStatusEnum directly — same pattern as LegacyDocumentsPage.tsx.
 * Filters match against `doc.apiStatus` (the original API status value),
 * except "Expired" which matches the `isRetentionExpired` flag.
 */
export const STATUS_OPTIONS = [
    { id: "all" as const, label: "All" },
    { id: DocumentStatusEnum.Pending, label: "Pending" },
    { id: DocumentStatusEnum.Parsed, label: "Parsed" },
    { id: DocumentStatusEnum.Queued, label: "Queued" },
    { id: DocumentStatusEnum.Exported, label: "Exported" },
    { id: DocumentStatusEnum.Error, label: "Error" },
    { id: DocumentStatusEnum.Rejected, label: "Rejected" },
    { id: EXPIRED_STATUS_FILTER, label: "Expired" },
] as const;

export type StatusFilter = "all" | DocumentStatusEnum | typeof EXPIRED_STATUS_FILTER;

/**
 * Default sort order
 */
export const DEFAULT_SORT = "newest" as const;

/**
 * Default export format
 */
export const DEFAULT_EXPORT_FORMAT = "csv" as const;

/**
 * Sort options for the sort dropdown
 */
export const SORT_OPTIONS = [
    { id: "newest", label: "Newest" },
    { id: "oldest", label: "Oldest" },
    { id: "highest", label: "Highest amount" },
    { id: "lowest", label: "Lowest amount" },
] as const;

export type SortOption = (typeof SORT_OPTIONS)[number]["id"];
